import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import {
  adminCreateFramework,
  adminCreateProblem,
  listIndustries,
} from "../lib/api";

export default function AdminPage() {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);

  // Framework form
  const [fw, setFw] = useState({ name: "", definition: "", matrix: "", how_to_use: "", generic_example: "" });
  const [fwMsg, setFwMsg] = useState(null);
  const [fwBusy, setFwBusy] = useState(false);

  // Problem form
  const [pb, setPb] = useState({ industry_id: "", title: "", description: "" });
  const [pbMsg, setPbMsg] = useState(null);
  const [pbBusy, setPbBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || !isAdmin) {
      navigate("/signin", { replace: true });
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      listIndustries().then(setIndustries).catch(() => {});
    }
  }, [isAdmin]);

  const submitFramework = async (e) => {
    e.preventDefault();
    setFwMsg(null);
    setFwBusy(true);
    try {
      const created = await adminCreateFramework(fw);
      setFwMsg({ type: "ok", text: `Framework #${created.framework_number} "${created.name}" created.` });
      setFw({ name: "", definition: "", matrix: "", how_to_use: "", generic_example: "" });
    } catch (err) {
      setFwMsg({ type: "err", text: err?.response?.data?.detail || err.message });
    } finally {
      setFwBusy(false);
    }
  };

  const submitProblem = async (e) => {
    e.preventDefault();
    setPbMsg(null);
    setPbBusy(true);
    try {
      const created = await adminCreateProblem({
        industry_id: parseInt(pb.industry_id, 10),
        title: pb.title,
        description: pb.description || null,
      });
      setPbMsg({ type: "ok", text: `Problem #${created.problem_number} created.` });
      setPb({ ...pb, title: "", description: "" });
    } catch (err) {
      setPbMsg({ type: "err", text: err?.response?.data?.detail || err.message });
    } finally {
      setPbBusy(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-12 pb-8 border-b border-ink-200" data-testid="admin-page">
        <p className="font-mono text-[10px] uppercase tracking-wide-3 text-vermilion">
          Admin panel · Restricted
        </p>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-medium text-ink-900 tracking-[-0.02em]">
          Manage library content.
        </h1>
        <p className="mt-3 text-sm text-ink-600 leading-relaxed max-w-2xl">
          Add new strategic frameworks to the library or extend any industry with additional documented problems.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-14 grid lg:grid-cols-2 gap-12">
        {/* Framework form */}
        <div className="border border-ink-200 bg-bone p-7" data-testid="add-framework-card">
          <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
            01 · New framework
          </p>
          <h2 className="mt-2 font-heading text-xl font-medium text-ink-900 tracking-tight">
            Add a strategic framework.
          </h2>

          <form onSubmit={submitFramework} className="mt-6 space-y-4" data-testid="framework-form">
            <Field label="Name" required>
              <input
                value={fw.name}
                onChange={(e) => setFw({ ...fw, name: e.target.value })}
                required
                data-testid="fw-name"
                className="input"
              />
            </Field>
            <Field label="Definition / When to use">
              <textarea
                value={fw.definition}
                onChange={(e) => setFw({ ...fw, definition: e.target.value })}
                rows={2}
                data-testid="fw-definition"
                className="input"
              />
            </Field>
            <Field label="Matrix / Visual description">
              <textarea
                value={fw.matrix}
                onChange={(e) => setFw({ ...fw, matrix: e.target.value })}
                rows={2}
                data-testid="fw-matrix"
                className="input"
              />
            </Field>
            <Field label="How to use (one step per line)">
              <textarea
                value={fw.how_to_use}
                onChange={(e) => setFw({ ...fw, how_to_use: e.target.value })}
                rows={3}
                data-testid="fw-how"
                className="input"
              />
            </Field>
            <Field label="Generic example">
              <textarea
                value={fw.generic_example}
                onChange={(e) => setFw({ ...fw, generic_example: e.target.value })}
                rows={2}
                data-testid="fw-example"
                className="input"
              />
            </Field>

            {fwMsg && (
              <p
                data-testid="fw-message"
                className={`font-mono text-[11px] uppercase tracking-wide-2 ${fwMsg.type === "ok" ? "text-vermilion" : "text-ink-900"}`}
              >
                {fwMsg.text}
              </p>
            )}

            <button
              type="submit"
              disabled={fwBusy}
              data-testid="fw-submit"
              className="w-full inline-flex items-center justify-center px-5 py-3 bg-ink-900 text-white font-mono text-[11px] uppercase tracking-wide-2 hover:bg-ink-800 transition-colors disabled:opacity-60"
            >
              {fwBusy ? "Saving…" : "Add framework"}
            </button>
          </form>
        </div>

        {/* Problem form */}
        <div className="border border-ink-200 bg-bone p-7" data-testid="add-problem-card">
          <p className="font-mono text-[10px] uppercase tracking-wide-3 text-ink-500">
            02 · New problem
          </p>
          <h2 className="mt-2 font-heading text-xl font-medium text-ink-900 tracking-tight">
            Add a problem to an industry.
          </h2>

          <form onSubmit={submitProblem} className="mt-6 space-y-4" data-testid="problem-form">
            <Field label="Industry" required>
              <select
                value={pb.industry_id}
                onChange={(e) => setPb({ ...pb, industry_id: e.target.value })}
                required
                data-testid="pb-industry"
                className="input"
              >
                <option value="">Select an industry…</option>
                {industries.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    IND.{String(ind.id).padStart(2, "0")} — {ind.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Problem statement" required>
              <input
                value={pb.title}
                onChange={(e) => setPb({ ...pb, title: e.target.value })}
                required
                data-testid="pb-title"
                className="input"
              />
            </Field>
            <Field label="Description (optional)">
              <textarea
                value={pb.description}
                onChange={(e) => setPb({ ...pb, description: e.target.value })}
                rows={3}
                data-testid="pb-description"
                className="input"
              />
            </Field>

            {pbMsg && (
              <p
                data-testid="pb-message"
                className={`font-mono text-[11px] uppercase tracking-wide-2 ${pbMsg.type === "ok" ? "text-vermilion" : "text-ink-900"}`}
              >
                {pbMsg.text}
              </p>
            )}

            <button
              type="submit"
              disabled={pbBusy}
              data-testid="pb-submit"
              className="w-full inline-flex items-center justify-center px-5 py-3 bg-ink-900 text-white font-mono text-[11px] uppercase tracking-wide-2 hover:bg-ink-800 transition-colors disabled:opacity-60"
            >
              {pbBusy ? "Saving…" : "Add problem"}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #E4E4E7;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: #0A0A0A;
          font-family: 'IBM Plex Sans', sans-serif;
        }
        .input:focus {
          outline: none;
          border-color: #0A0A0A;
          box-shadow: 0 0 0 1px rgba(10,10,10,0.2);
        }
      `}</style>
    </Layout>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-wide-3 text-ink-500 mb-2">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}
